import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, take, timeout } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth.service';
import { AdminApiService, EnvironmentDetail, EnvironmentEvent, EnvironmentNotebooks, EnvironmentOperation } from 'src/app/shared/services/admin-api.service';
import { DeleteEnvironmentDialogComponent } from './delete-environment-dialog.component';

interface EnvironmentDetailDialogData {
  owner: string;
  name: string;
}

@Component({
  standalone: false,
  selector: 'app-environment-detail',
  templateUrl: './environment-detail.component.html',
  styleUrls: ['./environment-detail.component.scss'],
})
export class EnvironmentDetailComponent implements OnInit {
  @Input() embedded = false;
  @Input() owner = '';
  @Input() name = '';
  @Output() closed = new EventEmitter<void>();

  token = '';
  loading = false;
  environment: EnvironmentDetail | null = null;
  errorMessage = '';
  private actionPending = new Set<string>();

  events: EnvironmentEvent[] = [];
  eventsLoading = false;
  eventsError = '';

  notebookInfo: EnvironmentNotebooks | null = null;
  notebooksLoading = false;
  notebooksError = '';
  readonly inDialog: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminApiService: AdminApiService,
    private authService: AuthService,
    private firestore: Firestore,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    @Optional() private dialogRef: MatDialogRef<EnvironmentDetailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: EnvironmentDetailDialogData | null,
  ) {
    this.token = localStorage.getItem('token') || '';
    this.inDialog = !!dialogData;
  }

  ngOnInit(): void {
    if (this.dialogData) {
      this.embedded = true;
      this.owner = this.dialogData.owner;
      this.name = this.dialogData.name;
    }

    if (!this.token) {
      this.snackBar.open('Missing auth token. Please sign in again.', 'OK');
      this.router.navigate(['/sign-in']);
      return;
    }

    if (!this.embedded) {
      this.owner = this.route.snapshot.paramMap.get('owner') || '';
      this.name = this.route.snapshot.paramMap.get('name') || '';
    }

    const userEmail = this.authService.userData?.email;
    if (!userEmail) {
      this.snackBar.open('Missing user session. Please sign in again.', 'OK');
      this.router.navigate(['/sign-in']);
      return;
    }

    const sub = docData(doc(this.firestore, `users/${userEmail}`))
      .subscribe((user: any) => {
        sub.unsubscribe();
        if (!this.isAdminUser(user)) {
          this.snackBar.open('Admin access required.', 'OK');
          this.router.navigate(['/sign-in']);
          return;
        }
        this.loadEnvironment();
      });
  }

  loadEnvironment(): void {
    if (!this.owner || !this.name) {
      this.errorMessage = 'Missing environment identifier.';
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.adminApiService
      .describeEnvironment(this.token, this.owner, this.name)
      .pipe(take(1), timeout(30000), finalize(() => this.applyState(() => (this.loading = false))))
      .subscribe({
        next: (env) => this.applyState(() => {
          this.environment = env;
          this.notebookInfo = null;
          this.notebooksError = '';
          if ((env?.state || '').toUpperCase() === 'RUNNING') {
            this.loadNotebooks();
          } else {
            this.notebookInfo = {
              environment: this.name,
              state: env.state,
              reachable: false,
              message: `Environment is ${env.state}. Notebook activity is only available while the environment is running.`,
              notebooks: [],
            };
          }
          this.loadEvents();
        }),
        error: (error) => this.applyState(() => {
          this.errorMessage =
            error?.error?.error || error?.error?.message || error?.message || 'Failed to load environment.';
        }),
      });
  }

  private loadNotebooks(): void {
    const email = this.environment?.owner;
    this.notebookInfo = null;
    this.notebooksError = '';
    if (!email) {
      return;
    }
    this.notebooksLoading = true;
    this.adminApiService.getUserNotebooks(this.token, email).pipe(take(1), timeout(30000)).subscribe({
      next: (data) => this.applyState(() => {
        this.notebookInfo = (data || []).find((entry) => entry.environment === this.name) || null;
        this.notebooksLoading = false;
      }),
      error: (error) => this.applyState(() => {
        console.error('Failed to fetch environment notebooks:', error);
        this.notebooksError = 'Unable to load notebook activity for this environment.';
        this.notebooksLoading = false;
      }),
    });
  }

  private loadEvents(): void {
    this.events = [];
    this.eventsError = '';
    this.eventsLoading = true;
    this.adminApiService
      .getEnvironmentEvents(this.token, this.owner, this.name)
      .pipe(take(1), timeout(30000), finalize(() => this.applyState(() => (this.eventsLoading = false))))
      .subscribe({
        next: (events) => this.applyState(() => {
          this.events = events.sort((a, b) => {
            const aTime = new Date(a.timestamp).getTime();
            const bTime = new Date(b.timestamp).getTime();
            return bTime - aTime;
          });
        }),
        error: (error) => this.applyState(() => {
          console.error('Failed to fetch environment events:', error);
          this.eventsError = 'Unable to load event history for this environment.';
        }),
      });
  }

  goBack(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }

    if (this.embedded) {
      this.closed.emit();
      return;
    }
    this.router.navigate(['/admin']);
  }

  formatDate(value: any): string {
    if (!value) { return '-'; }
    let d: Date;
    if (typeof value === 'object' && '_seconds' in value) {
      d = new Date(value._seconds * 1000 + (value._nanoseconds || 0) / 1000000);
    } else {
      d = new Date(value);
    }
    if (isNaN(d.getTime())) { return String(value); }
    return d.toLocaleString();
  }

  formatMetadataValue(value: unknown): string {
    if (value === null || value === undefined) { return '-'; }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  formatInitiator(event: EnvironmentEvent): string {
    if (event.initiator === 'SYSTEM') {
      const email = event.actorEmail || event.actorId || 'unknown';
      return `SYSTEM (admin api) - ${email}`;
    }
    return 'USER';
  }

  isActionBusy(key: string): boolean {
    return this.actionPending.has(key);
  }

  get anyActionBusy(): boolean {
    return this.actionPending.size > 0;
  }

  canResume(): boolean {
    const s = (this.environment?.state || '').toLowerCase();
    return s === 'stopped' || s === 'failed';
  }

  canStop(): boolean {
    const s = (this.environment?.state || '').toLowerCase();
    return s === 'running';
  }

  canDelete(): boolean {
    const s = (this.environment?.state || '').toLowerCase();
    return s !== 'terminating' && s !== 'terminated';
  }

  resumeEnvironment(): void {
    this.runOperation('restart', 'Environment resumed.');
  }

  stopEnvironment(): void {
    this.runOperation('stop', 'Environment stopped.');
  }

  confirmDelete(): void {
    const dialogRef = this.dialog.open(DeleteEnvironmentDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: { environmentName: this.environment?.name || '' }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.deleteEnvironment();
      }
    });
  }

  deleteEnvironment(): void {
    this.runOperation('delete', 'Environment deleted.');
  }

  private runOperation(operation: EnvironmentOperation, successMessage: string): void {
    if (!this.environment) { return; }
    const key = operation;
    this.actionPending.add(key);
    this.adminApiService
      .modifyEnvironments(this.token, [{ operation, environment: this.name, owner: this.owner }])
      .pipe(take(1), timeout(30000), finalize(() => this.applyState(() => this.actionPending.delete(key))))
      .subscribe({
        next: (results) => this.applyState(() => {
          const failed = (results || []).find((r) => r && r.status === 'failed');
          if (failed) {
            this.snackBar.open(failed.reason || 'Operation failed.', 'OK');
          } else {
            this.snackBar.open(successMessage, 'OK');
          }
          this.loadEnvironment();
        }),
        error: (error) => this.applyState(() => {
          const msg = error?.error?.error || error?.error?.message || error?.message || 'Operation failed.';
          this.snackBar.open(msg, 'OK');
        }),
      });
  }

  private applyState(mutate: () => void): void {
    mutate();
    try {
      this.cdr.detectChanges();
    } catch {
      /* detectChanges() throws if called re-entrantly during an existing CD pass; safe to ignore. */
    }
  }

  getAge(): string {
    if (!this.environment?.createDate) { return '-'; }
    const created = new Date(this.environment.createDate);
    const diffMs = Date.now() - created.getTime();
    if (isNaN(diffMs)) { return '-'; }
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) { return `${days}d ${hours}h`; }
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }

  Object = Object;

  private isAdminUser(user: any): boolean {
    if (!user) { return false; }
    return Boolean(
      user.isAdmin
      || user.admin
      || (typeof user.role === 'string' && user.role.toLowerCase() === 'admin')
      || (Array.isArray(user.roles) && user.roles.map((role: string) => role.toLowerCase()).includes('admin'))
    );
  }
}
